import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, BarChart3, Shield, FileCheck, Cpu, Workflow, CheckCircle2, Zap, Lock, LineChart, Activity, Globe, Server, Code2, Binary, Sparkles, Terminal, Layers, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState, useRef } from 'react';

// Animated Grid Background with pulsing nodes
function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base dark gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f0a] via-[#0d1a0d] to-[#0a0a0f]" />
      
      {/* Animated grid lines */}
      <svg className="absolute inset-0 w-full h-full opacity-30">
        <defs>
          <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(59, 162, 54, 0.3)" strokeWidth="0.5"/>
          </pattern>
          <linearGradient id="gridFade" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.1"/>
            <stop offset="50%" stopColor="white" stopOpacity="0.4"/>
            <stop offset="100%" stopColor="white" stopOpacity="0.1"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" mask="url(#gridFade)"/>
      </svg>
      
      {/* Glowing orbs */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-green/20 rounded-full blur-[150px]"
      />
      <motion.div 
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.5, 0.2]
        }}
        transition={{ duration: 5, repeat: Infinity }}
        className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-brown/20 rounded-full blur-[120px]"
      />
      <motion.div 
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.15, 0.4, 0.15]
        }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-1/2 right-1/3 w-64 h-64 bg-emerald-500/15 rounded-full blur-[100px]"
      />
    </div>
  );
}

// Floating particles system
function FloatingParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ 
            x: `${Math.random() * 100}%`, 
            y: `${Math.random() * 100}%`,
            scale: Math.random() * 0.5 + 0.5
          }}
          animate={{ 
            y: [null, `${Math.random() * -30 - 10}%`],
            opacity: [0, 0.8, 0]
          }}
          transition={{ 
            duration: Math.random() * 8 + 6,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "linear"
          }}
        >
          <div className={`w-1.5 h-1.5 rounded-full ${i % 3 === 0 ? 'bg-brand-green' : i % 3 === 1 ? 'bg-emerald-400' : 'bg-brand-brown/70'}`} />
        </motion.div>
      ))}
    </div>
  );
}

// Animated data flow lines
function DataFlowLines() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute h-px"
          style={{ 
            top: `${15 + i * 14}%`,
            width: '150%',
            left: '-25%',
            background: `linear-gradient(90deg, transparent, ${i % 2 === 0 ? 'rgba(59, 162, 54, 0.6)' : 'rgba(166, 83, 58, 0.4)'}, transparent)`
          }}
          animate={{ x: ['-25%', '25%'] }}
          transition={{ 
            duration: 6 + i * 1.5,
            repeat: Infinity,
            ease: "linear",
            delay: i * 0.5
          }}
        />
      ))}
    </div>
  );
}

// 3D Floating Dashboard Card
function FloatingDashboard({ delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 100, rotateX: 45, rotateY: -15 }}
      animate={{ opacity: 1, y: 0, rotateX: 12, rotateY: -8 }}
      transition={{ delay, duration: 1.2, ease: "easeOut" }}
      className="relative"
      style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
    >
      <motion.div
        animate={{ 
          y: [-8, 8, -8],
          rotateX: [12, 15, 12],
          rotateY: [-8, -5, -8]
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative"
      >
        {/* Main dashboard card */}
        <div className="w-80 md:w-96 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Header bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/5">
            <div className="w-3 h-3 rounded-full bg-red-500/80" />
            <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <div className="w-3 h-3 rounded-full bg-brand-green/80" />
            <span className="ml-3 text-xs font-mono text-white/50">ESG_Dashboard.tsx</span>
          </div>
          
          {/* Dashboard content */}
          <div className="p-5 space-y-4">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3">
              <motion.div 
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="bg-brand-green/20 rounded-lg p-3 border border-brand-green/30"
              >
                <div className="text-xl font-bold text-brand-green">94%</div>
                <div className="text-[10px] text-white/60">Compliance</div>
              </motion.div>
              <motion.div 
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className="bg-emerald-500/20 rounded-lg p-3 border border-emerald-500/30"
              >
                <div className="text-xl font-bold text-emerald-400">2.4k</div>
                <div className="text-[10px] text-white/60">Data Points</div>
              </motion.div>
              <motion.div 
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="bg-brand-brown/20 rounded-lg p-3 border border-brand-brown/30"
              >
                <div className="text-xl font-bold text-brand-brown">-18%</div>
                <div className="text-[10px] text-white/60">Emissions</div>
              </motion.div>
            </div>
            
            {/* Chart visualization */}
            <div className="bg-black/30 rounded-lg p-4 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-brand-green">Carbon Metrics</span>
                <Activity className="w-4 h-4 text-brand-green" />
              </div>
              <div className="flex items-end gap-1 h-16">
                {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((height, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: delay + 0.5 + i * 0.05, duration: 0.5 }}
                    className="flex-1 rounded-t bg-gradient-to-t from-brand-green to-emerald-400"
                  />
                ))}
              </div>
            </div>
            
            {/* Status indicators */}
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <motion.div 
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-brand-green"
                />
                <span className="text-white/60">Live Sync</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-emerald-400" />
                <span className="text-white/60">Verified</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating mini cards */}
        <motion.div
          animate={{ y: [-5, 5, -5], x: [0, 3, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          className="absolute -top-6 -right-8 bg-gradient-to-br from-brand-green/30 to-brand-green/10 backdrop-blur-lg rounded-xl p-3 border border-brand-green/40 shadow-lg"
        >
          <Zap className="w-5 h-5 text-brand-green" />
        </motion.div>
        
        <motion.div
          animate={{ y: [5, -5, 5], x: [0, -3, 0] }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          className="absolute -bottom-4 -left-6 bg-gradient-to-br from-brand-brown/30 to-brand-brown/10 backdrop-blur-lg rounded-xl p-3 border border-brand-brown/40 shadow-lg"
        >
          <Database className="w-5 h-5 text-brand-brown" />
        </motion.div>
        
        {/* Glow effect */}
        <div className="absolute inset-0 -z-10 blur-3xl bg-gradient-to-br from-brand-green/20 to-brand-brown/10 rounded-full scale-150" />
      </motion.div>
    </motion.div>
  );
}

// Animated code snippet
function AnimatedCodeBlock() {
  const codeLines = [
    { text: "const", highlight: "keyword" },
    { text: " esgData", highlight: "variable" },
    { text: " = ", highlight: "normal" },
    { text: "await", highlight: "keyword" },
    { text: " fetchMetrics", highlight: "function" },
    { text: "();", highlight: "normal" },
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.8, duration: 0.6 }}
      className="absolute bottom-20 left-10 hidden lg:block"
    >
      <motion.div
        animate={{ y: [-3, 3, -3] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="bg-black/60 backdrop-blur-xl rounded-xl border border-white/10 p-4 shadow-2xl"
      >
        <div className="flex items-center gap-2 mb-3">
          <Terminal className="w-4 h-4 text-brand-green" />
          <span className="text-xs font-mono text-white/50">dMRV.module.ts</span>
        </div>
        <div className="font-mono text-sm">
          <span className="text-purple-400">const </span>
          <span className="text-blue-300">metrics</span>
          <span className="text-white/70"> = </span>
          <span className="text-purple-400">await </span>
          <span className="text-yellow-300">validateData</span>
          <span className="text-white/70">(</span>
          <span className="text-brand-green">esg</span>
          <span className="text-white/70">);</span>
        </div>
        <motion.div
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="w-2 h-4 bg-brand-green mt-1"
        />
      </motion.div>
    </motion.div>
  );
}

// Orbiting icons
function OrbitingIcons() {
  const icons = [
    { Icon: Globe, delay: 0 },
    { Icon: Server, delay: 0.5 },
    { Icon: Layers, delay: 1 },
    { Icon: GitBranch, delay: 1.5 },
  ];
  
  return (
    <div className="absolute right-10 top-1/2 -translate-y-1/2 hidden xl:block">
      <div className="relative w-32 h-32">
        {icons.map(({ Icon, delay }, i) => (
          <motion.div
            key={i}
            animate={{ 
              rotate: 360,
            }}
            transition={{ 
              duration: 20, 
              repeat: Infinity, 
              ease: "linear",
              delay: delay * 5
            }}
            className="absolute inset-0"
            style={{ transformOrigin: 'center center' }}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay }}
              className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-lg rounded-xl p-2.5 border border-white/20"
            >
              <Icon className="w-4 h-4 text-brand-green" />
            </motion.div>
          </motion.div>
        ))}
        {/* Center glow */}
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute inset-0 bg-brand-green/30 rounded-full blur-2xl"
        />
      </div>
    </div>
  );
}

// Feature card component
function FeatureCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group relative"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-brand-green/20 to-brand-brown/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative bg-white border border-gray-200 rounded-2xl p-6 h-full hover:border-brand-green/30 transition-all duration-300 shadow-sm">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-green to-emerald-600 flex items-center justify-center mb-4 group-hover:shadow-lg group-hover:shadow-brand-green/30 transition-shadow duration-300">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-sans font-semibold text-brand-dark mb-2">{title}</h3>
        <p className="font-sans text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

function DigitalSolutionsPage() {
  return (
    <main data-testid="digital-solutions-page" className="overflow-hidden">
      {/* Hero Section - Futuristic Tech Theme */}
      <section className="relative min-h-[70vh] flex items-center pt-6 pb-10 mt-20 overflow-hidden">
        <GridBackground />
        <FloatingParticles />
        <DataFlowLines />
        <OrbitingIcons />
        
        <div className="relative z-10 w-full px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left content */}
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                className="mb-6"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green/10 border border-brand-green/30 backdrop-blur-lg">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  >
                    <Cpu className="w-4 h-4 text-brand-green" />
                  </motion.div>
                  <span className="font-mono text-xs font-medium text-brand-green tracking-wide">Services / Digital Solutions</span>
                </span>
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.1 }} 
                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6"
              >
                <span className="block">Digital</span>
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-brand-green via-emerald-400 to-brand-brown bg-clip-text text-transparent">
                    Solutions
                  </span>
                  <motion.span 
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                    className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-brand-green to-brand-brown rounded-full"
                  />
                  <motion.div
                    animate={{ x: ['0%', '100%', '0%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -bottom-2 left-0 w-8 h-1 bg-white/80 rounded-full blur-sm"
                  />
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.2 }} 
                className="font-mono text-lg md:text-xl text-brand-green mb-4"
              >
                Customized digital platforms for ESG KPIs and dMRV
              </motion.p>

              <motion.p 
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.25 }} 
                className="font-sans text-base text-white/70 max-w-lg mb-8 leading-relaxed"
              >
                Build your own digital system to manage ESG data, track performance, and run robust dMRV workflows—designed around your disclosures, projects, and operating reality.
              </motion.p>

              {/* Feature pills */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.3 }}
                className="flex flex-wrap gap-3 mb-8"
              >
                {['Real-time Monitoring', 'Automated Reports', 'Audit Trails'].map((feature, i) => (
                  <motion.span
                    key={feature}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/80 font-sans"
                  >
                    <CheckCircle2 className="w-3 h-3 text-brand-green" />
                    {feature}
                  </motion.span>
                ))}
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/contact">
                  <Button 
                    data-testid="hero-cta-build"
                    className="group bg-gradient-to-r from-brand-green to-emerald-500 hover:from-brand-green hover:to-emerald-400 text-white px-7 py-6 rounded-full shadow-2xl shadow-brand-green/30 text-sm font-semibold transition-all duration-300 hover:shadow-brand-green/50"
                  >
                    Build Your Platform 
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button 
                  data-testid="hero-cta-demo"
                  variant="outline" 
                  className="group px-7 py-6 rounded-full border-2 border-white/20 text-white hover:bg-white/10 text-sm backdrop-blur-md hover:border-brand-green/50 transition-all duration-300"
                >
                  <Sparkles className="w-4 h-4 mr-2 group-hover:text-brand-green transition-colors" /> 
                  See Demo
                </Button>
              </motion.div>
            </div>

            {/* Right side - 3D Dashboard */}
            <div className="relative flex justify-center lg:justify-end">
              <FloatingDashboard delay={0.3} />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 1.5 }} 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div 
            animate={{ y: [0, 10, 0] }} 
            transition={{ duration: 2, repeat: Infinity }} 
            className="flex flex-col items-center text-white/50"
          >
            <span className="text-xs uppercase tracking-wider mb-2 font-mono">Scroll</span>
            <div className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center p-1">
              <motion.div 
                animate={{ y: [0, 12, 0] }} 
                transition={{ duration: 2, repeat: Infinity }} 
                className="w-1 h-2 rounded-full bg-brand-green" 
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* What This Includes - White Section */}
      <section className="py-16 relative overflow-hidden bg-white">
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="text-center mb-12"
          >
            <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">Features</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark mt-3">
              What This <span className="italic bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Includes</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={Database}
              title="Automated Data Capture"
              description="Automated ESG and project-level data capture from multiple sources"
              delay={0}
            />
            <FeatureCard 
              icon={Shield}
              title="Built-in QA/QC"
              description="Quality checks, controls, and complete audit trails"
              delay={0.1}
            />
            <FeatureCard 
              icon={Workflow}
              title="dMRV Workflows"
              description="Methodology-aligned digital MRV workflows"
              delay={0.2}
            />
            <FeatureCard 
              icon={FileCheck}
              title="Registry-Ready Outputs"
              description="Reporting- and registry-ready outputs for compliance"
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section - Dual Platform */}
      <section className="py-24 relative overflow-hidden bg-white">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-green/50 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="inline-block font-mono text-xs font-semibold text-brand-brown uppercase px-4 py-2 rounded-full bg-brand-brown/10 mb-4">
              Platform Preview
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark">
              Tailored for <span className="bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Every Need</span>
            </h2>
            <p className="font-sans text-base text-gray-600 mt-4 max-w-2xl mx-auto">
              Whether you're managing ESG disclosures or carbon projects, our platform adapts to your requirements
            </p>
          </motion.div>

          {/* Dual Platform Grid */}
          <div className="grid lg:grid-cols-2 gap-8">
            
            {/* ESG Platform */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="group relative bg-gradient-to-br from-brand-surface to-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-brand-green/10 transition-all duration-500"
            >
              {/* Header badge */}
              <div className="absolute top-4 left-4 z-10">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-green/90 text-white text-xs font-semibold shadow-lg">
                  <BarChart3 className="w-3.5 h-3.5" />
                  For ESG
                </span>
              </div>
              
              {/* Dashboard Image */}
              <div className="relative h-52 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80" 
                  alt="ESG Dashboard" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-xl text-brand-dark mb-3">
                  ESG Data Platform
                </h3>
                <p className="font-sans text-sm text-gray-600 mb-5">
                  Comprehensive ESG KPI tracking, disclosure management, and sustainability reporting in one place.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="text-gray-700">Real-time ESG KPI Dashboards</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="text-gray-700">Secure & Compliant Data Storage</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                      <FileCheck className="w-4 h-4 text-brand-green" />
                    </div>
                    <span className="text-gray-700">Automated Disclosure Reports</span>
                  </div>
                </div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-brand-green/10 rounded-full blur-2xl group-hover:bg-brand-green/20 transition-colors duration-500" />
            </motion.div>

            {/* Carbon Projects Platform */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="group relative bg-gradient-to-br from-brand-surface to-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-brand-brown/10 transition-all duration-500"
            >
              {/* Header badge */}
              <div className="absolute top-4 left-4 z-10">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-brown/90 text-white text-xs font-semibold shadow-lg">
                  <Activity className="w-3.5 h-3.5" />
                  For Carbon Projects
                </span>
              </div>
              
              {/* Dashboard Image */}
              <div className="relative h-52 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1586448910234-297fae7189e6?w=800&q=80" 
                  alt="Carbon Projects Dashboard" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-xl text-brand-dark mb-3">
                  Carbon Projects Platform
                </h3>
                <p className="font-sans text-sm text-gray-600 mb-5">
                  End-to-end dMRV for carbon projects with methodology-aligned workflows and registry-ready outputs.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-brown/10 flex items-center justify-center flex-shrink-0">
                      <Workflow className="w-4 h-4 text-brand-brown" />
                    </div>
                    <span className="text-gray-700">dMRV Workflow Automation</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-brown/10 flex items-center justify-center flex-shrink-0">
                      <Database className="w-4 h-4 text-brand-brown" />
                    </div>
                    <span className="text-gray-700">Project-level Data Capture</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 rounded-lg bg-brand-brown/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-brand-brown" />
                    </div>
                    <span className="text-gray-700">Registry-Ready Credit Outputs</span>
                  </div>
                </div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-brand-brown/10 rounded-full blur-2xl group-hover:bg-brand-brown/20 transition-colors duration-500" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Outcome Section - White */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-brand-surface via-white to-brand-surface">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-green/10 rounded-full blur-[100px]"
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">Outcome</span>
            
            <h2 className="font-serif text-3xl sm:text-4xl text-brand-dark mt-3 mb-8">
              Audit-ready data. <span className="text-brand-green italic">Faster reporting.</span> Higher credibility.
            </h2>

            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-brand-green/30 hover:shadow-lg transition-all"
              >
                <div className="text-4xl font-serif text-brand-green mb-2">10x</div>
                <p className="font-sans text-sm text-gray-600">Faster data collection</p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-brand-green/30 hover:shadow-lg transition-all"
              >
                <div className="text-4xl font-serif text-brand-green mb-2">100%</div>
                <p className="font-sans text-sm text-gray-600">Audit trail coverage</p>
              </motion.div>
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-brand-green/30 hover:shadow-lg transition-all"
              >
                <div className="text-4xl font-serif text-brand-green mb-2">24/7</div>
                <p className="font-sans text-sm text-gray-600">Automated monitoring</p>
              </motion.div>
            </div>

            <Link to="/contact">
              <Button 
                data-testid="outcome-cta"
                className="group bg-gradient-to-r from-brand-green to-emerald-500 text-white px-8 py-6 rounded-full text-base font-semibold shadow-xl shadow-brand-green/20 hover:shadow-brand-green/40 transition-all"
              >
                Start Building Your Platform 
                <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export default DigitalSolutionsPage;
